import { useEffect, useState } from 'react';
import { fetchCommunityFeed, type CommunityFeedItem } from '@/lib/feed';
import { PRODUCT_NAME } from '@/lib/brand';

function formatDate(ms: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60 * 1000) return 'Just now';
  if (diff < 60 * 60 * 1000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 24 * 60 * 60 * 1000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 7 * 24 * 60 * 60 * 1000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
}

export default function Feed() {
  const [items, setItems] = useState<CommunityFeedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCommunityFeed(30)
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load feed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="max-w-4xl">
      <h1 className="font-display mb-2 text-2xl font-semibold tracking-wide text-mirror-text">Community feed</h1>
      <p className="mb-6 text-mirror-muted">
        Try-ons shared by the {PRODUCT_NAME} community. Use &quot;Try this look&quot; to open the extension with that garment ready, or &quot;View product&quot; to open the store page.
      </p>

      {loading && (
        <div className="rounded-xl border border-mirror-border bg-mirror-card py-12 text-center text-mirror-muted">
          Loading feed…
        </div>
      )}
      {error && (
        <div className="rounded-xl border border-mirror-border bg-mirror-card p-4 text-red-300 border-l-4 border-l-red-500/80">
          {error}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="rounded-xl border border-mirror-border bg-mirror-card py-12 text-center text-mirror-muted">
          No shared try-ons yet. Be the first to share from the extension after generating a look.
        </div>
      )}
      {!loading && !error && items.length > 0 && (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <FeedCard key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

function FeedCard({ item }: { item: CommunityFeedItem }) {
  const [trySent, setTrySent] = useState(false);
  const hasProductLink = !!item.productPageUrl && item.productPageUrl.startsWith('http');
  const hasGarmentLink = !!item.garmentImageUrl && item.garmentImageUrl.startsWith('http');

  function handleTryThisLook(e: React.MouseEvent) {
    e.preventDefault();
    try {
      document.dispatchEvent(
        new CustomEvent('shopmi-try-this-look', {
          detail: {
            garmentImageUrl: item.garmentImageUrl || '',
            productPageUrl: item.productPageUrl || '',
          },
        })
      );
      setTrySent(true);
    } catch {
      // ignore
    }
  }

  return (
    <article className="overflow-hidden rounded-xl border border-mirror-border bg-mirror-card transition-[border-color] hover:border-[rgba(201,169,98,0.3)]">
      <div className="aspect-square bg-mirror-surface">
        <img
          src={item.imageUrl}
          alt="Community try-on"
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="p-3">
        <p className="mb-2 text-xs text-mirror-muted">
          {formatDate(item.createdAt)}
        </p>
        {trySent && (
          <p className="mb-2 text-xs text-emerald-400">
            Garment sent — click the {PRODUCT_NAME} extension icon to try it.
          </p>
        )}
        <div className="flex flex-wrap gap-2">
          {hasGarmentLink && (
            <button
              type="button"
              onClick={handleTryThisLook}
              className="rounded-lg bg-mirror-gold px-3 py-2 text-sm font-medium text-mirror-bg hover:bg-mirror-gold-hover transition-colors"
            >
              Try this look
            </button>
          )}
          {hasProductLink && (
            <a
              href={item.productPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-lg border border-mirror-border bg-mirror-surface px-3 py-2 text-sm font-medium text-mirror-text hover:bg-mirror-border transition-colors"
            >
              View product
            </a>
          )}
          {!hasGarmentLink && !hasProductLink && (
            <span className="text-sm text-mirror-muted">Links not available</span>
          )}
        </div>
      </div>
    </article>
  );
}
